import { ExternalLink } from "lucide-react"
import { useState } from "react"
import { BusinessTypeEnum } from "~/__generated__/graphql"
import {
  ROBIN_DASHBOARD_URL,
  ROBIN_MARKETING_URL,
  robinConnectUrl,
} from "~/api/robin"
import { useCurrentUser } from "~/auth/CurrentUserContext"
import { useHeaderText } from "~/common/HeaderContext"
import { useTrackScreenEvent } from "~/common/useTrackScreenEvent"
import { ConnectRobinModal } from "~/screens/modals/ConnectRobinModal"
import { Badge } from "~/shadcn/ui/badge"
import { Button } from "~/shadcn/ui/button"
import { Card, CardContent } from "~/shadcn/ui/card"
import { IntegrationCard } from "./components/IntegrationCard"

import googleLogo from "~/images/integrations/google"
import metaLogo from "~/images/integrations/meta"
import robinLogo from "~/images/integrations/robin"
import slackLogo from "~/images/integrations/slack"
import tiktokLogo from "~/images/integrations/tiktok"

const comingSoonIntegrations = [
  {
    logo: metaLogo,
    title: "Meta API",
    description:
      "Get direct Instagram API access and let creators confirm your agency represents them with a verified badge.",
  },
  {
    logo: tiktokLogo,
    title: "TikTok API",
    description:
      "Get direct Tiktok API access and let creators confirm your agency represents them with a verified badge.",
  },
  {
    logo: googleLogo,
    title: "Google API",
    description:
      "Get direct YouTube API access and let creators confirm your agency represents them with a verified badge.",
  },
  {
    logo: slackLogo,
    title: "Slack",
    description:
      "Publish casting calls to Slack communities and Zano simultaneously from a single workflow.",
  },
]

const VIEW_SETTINGS_PROPS = { section: "integrations" }

export const IntegrationsScreen = () => {
  useTrackScreenEvent("view_settings", VIEW_SETTINGS_PROPS)
  useHeaderText({ header: "Integrations", subheader: null })
  const currentUser = useCurrentUser()
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false)

  const robinConnected = currentUser.agency?.robinConnected ?? false
  const robinPending = currentUser.agency?.robinConnectPending ?? false
  const agencySlug = currentUser.agency?.slug ?? ""

  // Robin (and the not-yet-live API integrations) target talent agencies.
  // Marketing agencies and brands only get Slack.
  const isTalentAgency =
    currentUser.agency?.businessType === BusinessTypeEnum.Talent
  const visibleComingSoon = isTalentAgency
    ? comingSoonIntegrations
    : comingSoonIntegrations.filter(
        (integration) => integration.title === "Slack"
      )

  const robinBadge = robinConnected ? (
    <Badge className="bg-greenLight text-greenDark border-transparent">
      Connected
    </Badge>
  ) : robinPending ? (
    <Badge className="bg-gray20 text-gray50 border-transparent">Pending</Badge>
  ) : undefined

  const robinFooter = robinConnected ? (
    <Button variant="secondary" className="ml-auto" asChild>
      <a href={ROBIN_DASHBOARD_URL} target="_blank" rel="noopener noreferrer">
        Open Robin
        <ExternalLink className="w-4 h-4 ml-2" />
      </a>
    </Button>
  ) : robinPending ? (
    <>
      <span className="text-sm text-gray50">Waiting for Robin to confirm</span>
      <Button variant="secondary" asChild>
        <a
          href={robinConnectUrl(agencySlug)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open Robin
          <ExternalLink className="w-4 h-4 ml-2" />
        </a>
      </Button>
    </>
  ) : (
    <>
      <a
        href={ROBIN_MARKETING_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-sm text-gray50 hover:text-defaultPrimary"
      >
        Learn more
        <ExternalLink className="w-3 h-3" />
      </a>
      <Button variant="secondary" onClick={() => setIsConnectModalOpen(true)}>
        Connect
      </Button>
    </>
  )

  return (
    <div className="h-full pb-4 relative">
      <Card className="absolute left-0 right-0 top-0 bottom-4 overflow-auto">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {isTalentAgency && (
              <IntegrationCard
                logo={<img {...robinLogo} alt="Robin AI" />}
                title="Robin AI"
                description="AI agent that qualifies leads and negotiates brand deals using your live Zano media kit."
                className={robinConnected ? "bg-greenLight/30" : undefined}
                badge={robinBadge}
                footer={robinFooter}
              />
            )}

            {visibleComingSoon.map((integration) => (
              <IntegrationCard
                key={integration.title}
                logo={
                  <img
                    {...integration.logo}
                    alt={`${integration.title} logo`}
                  />
                }
                title={integration.title}
                description={integration.description}
                footer={
                  <Button
                    variant="outline"
                    className="ml-auto italic text-gray50"
                    disabled
                  >
                    Coming Soon
                  </Button>
                }
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {isTalentAgency && (
        <ConnectRobinModal
          open={isConnectModalOpen}
          onClose={() => setIsConnectModalOpen(false)}
        />
      )}
    </div>
  )
}
